import React, { useState } from 'react'

import {
  AlignItems,
  type BorderScale,
  BorderStyle,
  Body2,
  Box,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Icon,
  IconVariant,
  Image,
  Link,
  LinkVariant,
  type RadiusScale,
} from '@gousto-internal/citrus-react'
import { CopyToClipboard } from 'react-copy-to-clipboard'

import type { OrderConfirmationRafSuccessMessage } from '@library/api-customer-engagement-experience'

import { getReferralLink } from 'components/SocialLinks/socialReferralHelper'

import copyLinkIcon from './assets/copy-link-icon.svg'

type DesktopCopyShareLinkProps = {
  referralCode: string
  label: string
  successMessage: OrderConfirmationRafSuccessMessage
  onClick: () => void
  onCopy: () => void
}

export const DesktopCopyShareLink = ({
  referralCode,
  label,
  successMessage,
  onClick,
  onCopy,
}: DesktopCopyShareLinkProps) => {
  const [copied, setCopied] = useState(false)
  const referralLink = getReferralLink(referralCode, '', '&utm_source=weblink')

  // onClick fires regardless of result — mirrors the mobile CTA, which counts the click-analytics
  // event on activation, not on the outcome. onCopy (the success UI + LoggingManager event) stays
  // conditional on the clipboard write actually succeeding.
  const handleCopy = (_text: string, result: boolean) => {
    onClick()

    if (!result) {
      return
    }

    setCopied(true)
    onCopy()
  }

  if (copied) {
    const { title, subtitle, linkText } = successMessage

    // Built from Box/Icon/Body2, not CopyConfirmation: Figma (263:2848) nests the "Copy link
    // again" action inside the same bordered card as the message, which CopyConfirmation has no
    // slot for.
    return (
      <Box
        role="status"
        data-testid="rafLinkCopyConfirmation"
        bg={Color.Success_50}
        borderColor={Color.Success_900}
        borderStyle={BorderStyle.Solid}
        borderWidth={0.5 as BorderScale}
        borderRadius={4 as RadiusScale}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={3}
        paddingH={4}
        paddingV={4}
        width="100%"
      >
        <Box display={Display.Flex} alignItems={AlignItems.FlexStart} gap={2} width="100%">
          <Icon name="success" variant={IconVariant.Confirmation} size={6} />
          <Box display={Display.Flex} flexDirection={FlexDirection.Column} flexGrow={1}>
            <Body2 size={4} fontWeight={FontWeight.Bold} color={Color.Success_900}>
              {title}
            </Body2>
            <Body2 size={4} color={Color.ColdGrey_800}>
              {subtitle}
            </Body2>
          </Box>
        </Box>
        <Box display={Display.Flex} alignItems={AlignItems.Center} gap={2} paddingH={8}>
          <CopyToClipboard text={referralLink} onCopy={handleCopy}>
            <Link variant={LinkVariant.None}>
              <Box display={Display.Flex} alignItems={AlignItems.Center} gap={2}>
                <Body2 size={4} fontWeight={FontWeight.Bold} color={Color.Secondary_400}>
                  {linkText}
                </Body2>
                <Image src={copyLinkIcon} alt="" width="24px" height="24px" />
              </Box>
            </Link>
          </CopyToClipboard>
        </Box>
      </Box>
    )
  }

  return (
    <CopyToClipboard text={referralLink} onCopy={handleCopy}>
      <Button colorVariant={ButtonColorVariant.Secondary} width="100%" type="button">
        {label}
      </Button>
    </CopyToClipboard>
  )
}
